import { Injectable, inject } from "@angular/core";
import { differenceInCalendarDays, parse, format } from "date-fns";
import { merge } from "lodash-es";
import { CookieService } from "ngx-cookie-service";
import { Observable, of } from "rxjs";
import { tap } from "rxjs/operators";
import { LocaleService } from "./locale.service";
import { AnalyticsModel } from "@fpr/core/models/analytics.models";
import { ConfigService } from "./config.service";
import { GuestService } from "@fpr/core/http/guest.service";
import { VasService } from "./vas.service";
import { WINDOW } from "./window.factory";

@Injectable({
    providedIn: "root",
})
export class AnalyticsManager {
    private readonly configService = inject(ConfigService);
    private readonly cookieService = inject(CookieService);
    private readonly guestsService = inject(GuestService);
    private readonly localeService = inject(LocaleService);
    private readonly vasService = inject(VasService);
    private readonly window = inject<Window>(WINDOW);

    private model?: AnalyticsModel;
    private globalModel?: AnalyticsModel;
    private currentKey?: string;
    private framework?: any;

    constructor() {
        this.setFramework();

        this.globalModel = {
            customerId: this.cookieService.get("SWID"),
            registrationStatus: this.getRegistrationStatus(),
            contentLocale: this.localeService.region,
            contentLanguage: this.localeService.lang,
            device: {
                type: this.getDeviceType(),
                viewType: this.getDeviceViewType(),
            },
            configuration: {
                contentLocale: this.localeService.region,
                contentLanguage: this.localeService.lang,
                marketingRegion: this.localeService.region,
            },
            store: "WDWAdmission",
            guestType: "Guest",
        };
    }

    updateModel(modelRevisions: AnalyticsModel, pageKey?: string): void {
        if (this.configService.ENABLE_CIMA_ANALYTICS) {
            this.setFramework();

            this.getModel(pageKey || this.currentKey).subscribe(
                (currentModel) => {
                    const experienceId = this.guestsService.getActiveGuestXid();
                    this.model = merge(
                        {},
                        this.globalModel,
                        { experienceId },
                        currentModel,
                        modelRevisions,
                    );

                    if (this.configService.ENABLE_CIMA_LOGGING) {
                        console.log("UPDATED MODEL:", this.model);
                    }

                    if (this.framework) {
                        this.framework.reset();
                        this.framework.update(this.model);
                    }
                },
            );
        }
    }

    resetModel(): void {
        this.model = null;
    }

    trackClick(linkId: string): void {
        const trackLinkModel = merge({}, this.model, {
            linkId,
            trackingType: "customLink",
        });

        if (this.configService.ENABLE_CIMA_LOGGING) {
            console.log("TRACK LINK MODEL:", trackLinkModel);
        }

        this.setFramework();

        if (this.framework) {
            this.framework.trackElement(trackLinkModel);
        }
    }

    getModel(pageKey: string): Observable<AnalyticsModel> {
        this.currentKey = pageKey;

        if (this.model) {
            return of(this.model);
        }

        return this.getPageModel(pageKey);
    }

    getPageModel(key: string): Observable<AnalyticsModel> {
        return this.vasService
            .get(`/v1/assets/analytics/${key}`, {
                skipErrorInterceptor: true,
            })
            .pipe(tap((pageModel) => (this.model = pageModel)));
    }

    getNumOfDaysFromToday(date: string): number {
        return differenceInCalendarDays(
            parse(date, "yyyy-MM-dd", new Date()),
            new Date(),
        );
    }

    getFormattedDate(date: string): string {
        return format(parse(date, "yyyy-MM-dd", new Date()), "MM/dd/yyyy");
    }

    private setFramework(): void {
        if (!this.framework) {
            this.framework = this.window?.["WDPRO"]?.Analytics?.Framework;
        }
    }

    private getRegistrationStatus(): string {
        const swid = this.cookieService.get("SWID");
        const isRegistered =
            typeof swid === "string" && swid.substr(0, 1) === "{";
        const isLoggedIn = this.cookieService.get("authenticationSecure");

        if (isLoggedIn) {
            return "Registered - Logged In";
        } else if (isRegistered) {
            return "Registered - Not Logged In";
        }

        return "Not Registered";
    }

    private getDeviceType(): string {
        try {
            const view = JSON.parse(this.cookieService.get("WDPROView"));

            return (
                view?.deviceInfo?.device || view?.preferred?.device || "desktop"
            );
        } catch (_err) {
            return "desktop";
        }
    }

    private getDeviceViewType(): string {
        try {
            const view = JSON.parse(this.cookieService.get("WDPROView"));

            return view?.preferred?.device || "desktop";
        } catch (_err) {
            return "desktop";
        }
    }
}
